import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { RefreshCw, Loader2, CheckCircle, XCircle, Database, Download, FileJson } from "lucide-react";
import { useTranslation } from "react-i18next";
import StatusBadge from "./StatusBadge";

const SyncStatus = ({
  lastSyncTime,
  songsAdded,
  nextScheduledSync,
  syncType,
  status,
  onManualSync,
  isLoading,
  progress,
}) => {
  const { t } = useTranslation();
  const [syncStage, setSyncStage] = useState("connecting");
  const [progressPercent, setProgressPercent] = useState(0);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  
  // Update sync stage based on progress messages
  useEffect(() => {
    if (!progress) return;
    
    if (progress.includes("Connecting to MongoDB")) {
      setSyncStage("connecting");
      setProgressPercent(10);
    } else if (progress.includes("Accessing") || progress.includes("Parsing HTML")) {
      setSyncStage("accessing");
      setProgressPercent(20);
    } else if (progress.includes("Searching for database")) {
      setSyncStage("searching");
      setProgressPercent(30);
    } else if (progress.includes("Downloading ZIP")) {
      setSyncStage("downloading");
      setProgressPercent(40);
    } else if (progress.includes("Download complete")) {
      setSyncStage("downloaded");
      setProgressPercent(60);
    } else if (progress.includes("Extracting ZIP")) {
      setSyncStage("extracting");
      setProgressPercent(70);
    } else if (progress.includes("Loading songs")) {
      setSyncStage("loading");
      setProgressPercent(80);
    } else if (progress.includes("Fetching existing songs")) {
      setSyncStage("comparing");
      setProgressPercent(90);
    } else if (progress.includes("Script execution completed") || progress.includes("completed successfully")) {
      setSyncStage("completed");
      setProgressPercent(100);
      setShowCompletionMessage(true);
    }
    
    // Extract download percentage if available
    const downloadMatch = progress.match(/Downloading:\s+(\d+)%/);
    if (downloadMatch && downloadMatch[1]) {
      const downloadPercent = parseInt(downloadMatch[1], 10);
      setProgressPercent(40 + (downloadPercent * 0.2)); // Scale to fit our progress range (40-60%)
    }
  }, [progress]);
  
  // Handle completion message display
  useEffect(() => {
    if (showCompletionMessage) {
      const timer = setTimeout(() => {
        setShowCompletionMessage(false);
      }, 3000); // Show completion message for 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showCompletionMessage]);
  
  // Get appropriate icon and message for current sync stage
  const getSyncStageInfo = () => {
    switch(syncStage) {
      case "connecting":
        return {
          icon: <Database className="text-gray-400 w-8 h-8 animate-pulse" />,
          message: t("admin.syncingStatus"),
          description: "Connecting to database..."
        };
      case "accessing":
      case "searching":
        return {
          icon: <FileJson className="text-gray-400 w-8 h-8 animate-pulse" />,
          message: t("admin.syncingStatus"),
          description: "Locating database files..."
        };
      case "downloading":
        return {
          icon: <Download className="text-gray-400 w-8 h-8 animate-pulse" />,
          message: t("admin.syncingStatus"),
          description: "Downloading database files..."
        };
      case "downloaded":
      case "extracting":
        return {
          icon: <FileJson className="text-gray-400 w-8 h-8 animate-pulse" />,
          message: t("admin.syncingStatus"),
          description: "Processing database files..."
        };
      case "loading":
      case "comparing":
        return {
          icon: <Database className="text-gray-400 w-8 h-8 animate-pulse" />,
          message: t("admin.syncingStatus"),
          description: "Updating database..."
        };
      case "completed":
        return {
          icon: <CheckCircle className="text-green-500 w-8 h-8" />,
          message: t("admin.syncSuccess"),
          description: `Successfully added ${songsAdded} songs`
        };
      default:
        return {
          icon: <Loader2 className="text-gray-400 w-8 h-8 animate-spin" />,
          message: t("admin.syncing"),
          description: t("admin.syncingMessage")
        };
    }
  };
  
  const { icon, message, description } = getSyncStageInfo();
  
  return (
    <Card className="rounded-2xl border border-gray-100 shadow hover:shadow-lg transition-shadow bg-white">
      <CardContent className="p-4 sm:p-6 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2 sm:gap-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <RefreshCw className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
            <span className="text-lg sm:text-xl font-semibold text-gray-800">
              {t("admin.syncStatus")}
            </span>
            <StatusBadge status={status === "success" ? "resolved" : status === "failed" ? "dismissed" : "pending"}>
              {status === "success" ? t("admin.success") : status === "failed" ? t("admin.failed") : t("admin.pending")}
            </StatusBadge>
          </div>
          <Button
            onClick={onManualSync}
            disabled={isLoading}
            className="px-4 sm:px-5 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto mt-2 sm:mt-0"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("admin.syncing")}
              </div>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1 inline text-white" />
                {t("admin.sync")}
              </>
            )}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-2">
          <div className="bg-gray-100 rounded-xl p-3 sm:p-4 flex flex-col gap-1">
            <span className="text-gray-400 text-xs">{t("admin.lastSyncTime")}:</span>
            <span className="text-gray-800 text-base font-medium break-words">{lastSyncTime}</span>
          </div>
          <div className="bg-gray-100 rounded-xl p-3 sm:p-4 flex flex-col gap-1">
            <span className="text-gray-400 text-xs">{t("admin.syncType")}:</span>
            <span className="text-gray-800 text-base font-medium capitalize break-words">{syncType}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-2">
          <div className="bg-gray-100 rounded-xl p-3 sm:p-4 flex flex-col gap-1">
            <span className="text-gray-400 text-xs">{t("admin.songsAdded")}:</span>
            <span className="text-gray-800 text-base font-medium">{songsAdded}</span>
          </div>
          <div className="bg-gray-100 rounded-xl p-3 sm:p-4 flex flex-col gap-1">
            <span className="text-gray-400 text-xs">{t("admin.nextScheduledSync")}:</span>
            <span className="text-gray-800 text-base font-medium break-words">{nextScheduledSync}</span>
          </div>
        </div>
        {(isLoading || showCompletionMessage) && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-2 sm:p-0">
            <div className="bg-white/95 rounded-3xl p-4 sm:p-8 w-full max-w-xs sm:max-w-md shadow-2xl flex flex-col items-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-gray-100 mb-4">
                {icon}
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-center mb-3 text-gray-800">
                {message}
              </h3>
              <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 mb-4">
                <div 
                  className="bg-orange-500 h-2 sm:h-3 rounded-full transition-all duration-300 ease-in-out" 
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <p className="text-gray-700 text-center text-sm sm:text-base mb-2">
                {syncStage === "completed" ? t("admin.syncSuccess") : t("admin.syncingMessage")}
              </p>
              <div className="text-xs sm:text-sm text-gray-500 mb-2 text-center">
                {description}
              </div>
              {syncStage === "completed" && (
                <Button 
                  onClick={() => setShowCompletionMessage(false)}
                  className="mt-4 bg-green-500 hover:bg-green-600 text-white w-full sm:w-auto"
                >
                  {t("admin.close")}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SyncStatus;